import React, { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import MenuItem from "@mui/material/MenuItem";
import _ from "lodash";
import toast from "react-hot-toast";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import FormHeader from "views/components/formheader";
import FormButtons from "views/components/formbuttons";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import { hijriMonthsFull } from "@core/layouts/utils";

import { FormControl, InputLabel, Select } from "@mui/material";
import { isSpaceValidateTextField } from "@core/utils/Const";

const AddhijriCalendar = (props) => {
  const { open, toggle, heading, onSubmitHijriCalendar, type, updateData } =
    props;

  const defaultValues = {
    month: "",
    days: "",
    year: "",
  };

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmptyValues = useCallback(() => {
    setValue("month", "");
    setValue("days", "");
    setValue("year", "");
  }, [setValue]);

  const handleClose = () => {
    if (type === "add") {
      reset(defaultValues);
    }
    clearErrors();
    toggle();
  };

  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const split = _.split(updateData.month_year, "-", 2);
      const event = {
        month: split[0],
        year: split[1],
        days: updateData?.days,
      };
      setValue("month", event.month || "");
      setValue("year", event.year || "");
      setValue("days", event.days || "");
    }
  }, [type, updateData?.month_year, updateData?.days, setValue]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const onSubmit = (data) => {
    const payload = {
      date: `${data.month}-${data.year}`,
      days: data.days,
    };

    if (type === "add") {
      APICall("post", payload, EndPoints.addHijriDate)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitHijriCalendar();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        });
    } else {
      const payloads = {
        id: updateData.id,
        ...payload,
      };
      APICall("post", payloads, EndPoints.updateHijriDate)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitHijriCalendar();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        });
    }
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />

      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="month"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <InputLabel
                        id="validation-basic-select"
                        error={Boolean(errors.month)}
                        htmlFor="validation-basic-select"
                      >
                        Month
                      </InputLabel>
                      <Select
                        label={"Month"}
                        onChange={onChange}
                        error={Boolean(errors.month)}
                        labelId="validation-basic-select"
                        aria-describedby="validation-basic-select"
                        value={value}
                      >
                        {hijriMonthsFull.map((item, index) => (
                          <MenuItem value={item.no} key={index}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                />
                {errors.month && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="year"
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d{4}$/,
                    message: "Year must be in YYYY format",
                  },
                  validate: isSpaceValidateTextField,
                }}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    fullWidth
                    type="text"
                    label="Year"
                    placeholder="Year"
                    error={Boolean(errors.year)}
                  />
                )}
              />
              {errors.year && (
                <FormHelperText
                  sx={{ color: "error.main" }}
                  id="validation-basic-select"
                >
                  {errors.year?.message
                    ? errors.year?.message
                    : "This field is required"}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="days"
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: /^[0-9]+$/,
                    message: "Days must be a number",
                  },

                  validate: isSpaceValidateTextField,
                }}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    fullWidth
                    type="text"
                    label="Days"
                    placeholder="Enter days"
                    error={Boolean(errors.days)}
                  />
                )}
              />
              {errors.days && (
                <FormHelperText
                  sx={{ color: "error.main" }}
                  id="validation-basic-select"
                >
                  {errors.days?.message
                    ? errors.days?.message
                    : "This field is required"}
                </FormHelperText>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "1rem",
                paddingTop: "0.4rem",
                alignItems: "center",
                justifyContent: "space-between",
                display: "flex",
              }}
            ></Grid>

            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddhijriCalendar;
